/**
 * BrandConfig, unified per-embed branding contract.
 *
 * One resolved object drives accent, surface, typography, shape, density,
 * locale, units, currency and per-widget label overrides across every
 * widget. Backwards-compatible with the legacy `theme` / `accent` /
 * `currency` / `faq` URL params; missing fields fall back to defaults that
 * match today's appearance exactly.
 *
 * This module is pure (no React, no DOM) so it can be unit-tested and used
 * by both the embed runtime (`/embed/:toolId`) and the snippet builder.
 */

import { ACCENT_COLOR_THEMES, POPULAR_CURRENCIES } from "@/config/constants";
import { hexToHsl } from "@/lib/color";

// ─── Types ────────────────────────────────────────────────────────────────

export type ThemeMode = "light" | "dark" | "auto";
export type FontPreset =
  | "space-grotesk"
  | "inter"
  | "roboto"
  | "open-sans"
  | "jakarta"
  | "source-serif";
export type RadiusPreset = "none" | "sm" | "md" | "lg" | "xl";
export type DensityPreset = "comfortable" | "compact";
export type UnitsPreset = "metric" | "imperial";

export interface BrandConfig {
  theme: ThemeMode;
  /** Either a preset name (emerald, blue, …) or a custom `#RRGGBB`. */
  accent: string;
  /** Optional surface (card/background) override as `#RRGGBB`. */
  surface?: string;
  font: FontPreset;
  radius: RadiusPreset;
  density: DensityPreset;
  /** BCP-47 locale, e.g. `en-AU`. */
  locale: string;
  /** ISO-4217 currency code. Empty string = unset. */
  currency: string;
  units: UnitsPreset;
  /** Per-widget label overrides, keyed by tool slug then label key. */
  labels: Record<string, Record<string, string>>;
}

export type PartialBrand = Partial<BrandConfig>;

// ─── Defaults & allow-lists ──────────────────────────────────────────────

export const DEFAULT_BRAND: BrandConfig = {
  theme: "light",
  accent: "emerald",
  font: "space-grotesk",
  radius: "lg",
  density: "comfortable",
  locale: "en-AU",
  currency: "",
  units: "metric",
  labels: {},
};

export const FONT_PRESETS: Record<FontPreset, { display: string; body: string; googleFamily?: string }> = {
  "space-grotesk": { display: "'Space Grotesk', sans-serif", body: "'Inter', sans-serif" },
  inter:           { display: "'Inter', sans-serif",          body: "'Inter', sans-serif",
                     googleFamily: "Inter:wght@400;500;600;700" },
  roboto:          { display: "'Roboto', sans-serif",         body: "'Roboto', sans-serif",
                     googleFamily: "Roboto:wght@400;500;700" },
  "open-sans":     { display: "'Open Sans', sans-serif",      body: "'Open Sans', sans-serif",
                     googleFamily: "Open+Sans:wght@400;500;600;700" },
  jakarta:         { display: "'Plus Jakarta Sans', sans-serif", body: "'Plus Jakarta Sans', sans-serif",
                     googleFamily: "Plus+Jakarta+Sans:wght@400;500;600;700" },
  "source-serif":  { display: "'Source Serif 4', serif",      body: "'Source Serif 4', serif",
                     googleFamily: "Source+Serif+4:wght@400;500;600;700" },
};

export const RADIUS_PRESETS: Record<RadiusPreset, string> = {
  none: "0rem",
  sm:   "0.25rem",
  md:   "0.5rem",
  lg:   "0.75rem", // default, matches current --radius
  xl:   "1rem",
};

export const DENSITY_PRESETS: Record<DensityPreset, { inputH: string; gap: string }> = {
  comfortable: { inputH: "2.5rem",  gap: "1rem"    },
  compact:     { inputH: "2.25rem", gap: "0.75rem" },
};

// ─── Validators ──────────────────────────────────────────────────────────

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const LOCALE_RE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

export function isValidHex(v: unknown): v is string {
  return typeof v === "string" && HEX_RE.test(v.trim());
}
export function isAccentPreset(v: unknown): boolean {
  return typeof v === "string" && v in ACCENT_COLOR_THEMES;
}
export function isFontPreset(v: unknown): v is FontPreset {
  return typeof v === "string" && v in FONT_PRESETS;
}
export function isRadiusPreset(v: unknown): v is RadiusPreset {
  return typeof v === "string" && v in RADIUS_PRESETS;
}
export function isDensityPreset(v: unknown): v is DensityPreset {
  return v === "comfortable" || v === "compact";
}
export function isUnitsPreset(v: unknown): v is UnitsPreset {
  return v === "metric" || v === "imperial";
}
export function isThemeMode(v: unknown): v is ThemeMode {
  return v === "light" || v === "dark" || v === "auto";
}
export function isLocale(v: unknown): v is string {
  if (typeof v !== "string" || !LOCALE_RE.test(v)) return false;
  try { new Intl.NumberFormat(v); return true; } catch { return false; }
}
export function isCurrencyCode(v: unknown): v is string {
  return typeof v === "string" && POPULAR_CURRENCIES.includes(v.toUpperCase());
}

// ─── Resolution ──────────────────────────────────────────────────────────

/** Fill missing fields with defaults; drop invalid values silently. */
export function resolveBrand(partial: PartialBrand | null | undefined): BrandConfig {
  const p = partial ?? {};
  return {
    theme:    isThemeMode(p.theme) ? p.theme : DEFAULT_BRAND.theme,
    accent:   isAccentPreset(p.accent) || isValidHex(p.accent) ? p.accent! : DEFAULT_BRAND.accent,
    surface:  isValidHex(p.surface) ? p.surface : undefined,
    font:     isFontPreset(p.font) ? p.font : DEFAULT_BRAND.font,
    radius:   isRadiusPreset(p.radius) ? p.radius : DEFAULT_BRAND.radius,
    density:  isDensityPreset(p.density) ? p.density : DEFAULT_BRAND.density,
    locale:   isLocale(p.locale) ? p.locale : DEFAULT_BRAND.locale,
    currency: isCurrencyCode(p.currency) ? p.currency!.toUpperCase() : (p.currency || DEFAULT_BRAND.currency),
    units:    isUnitsPreset(p.units) ? p.units : DEFAULT_BRAND.units,
    labels:   isLabelMap(p.labels) ? p.labels! : DEFAULT_BRAND.labels,
  };
}

function isLabelMap(v: unknown): v is Record<string, Record<string, string>> {
  if (!v || typeof v !== "object") return false;
  for (const inner of Object.values(v as Record<string, unknown>)) {
    if (!inner || typeof inner !== "object") return false;
    for (const s of Object.values(inner as Record<string, unknown>)) {
      if (typeof s !== "string") return false;
    }
  }
  return true;
}

// ─── URL parsing ─────────────────────────────────────────────────────────

/** Decode a URL-safe base64 JSON payload; returns null on any failure. */
function decodeBase64Json(raw: string | null): unknown {
  if (!raw) return null;
  try {
    const normalised = raw.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = typeof atob === "function"
      ? atob(normalised)
      : Buffer.from(normalised, "base64").toString("utf8");
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Parse a `URLSearchParams` (or anything with a `.get()` method) into a
 * BrandConfig. Invalid values are dropped, never throws.
 */
export function parseBrandFromParams(params: URLSearchParams): BrandConfig {
  const get = (k: string) => params.get(k);
  const labelsRaw = get("labels");
  const labels = decodeBase64Json(labelsRaw);
  return resolveBrand({
    theme:    (get("theme") as ThemeMode) ?? undefined,
    accent:   get("accent") ?? undefined,
    surface:  get("surface") ?? undefined,
    font:     (get("font") as FontPreset) ?? undefined,
    radius:   (get("radius") as RadiusPreset) ?? undefined,
    density:  (get("density") as DensityPreset) ?? undefined,
    locale:   get("locale") ?? undefined,
    currency: get("currency") ?? undefined,
    units:    (get("units") as UnitsPreset) ?? undefined,
    labels:   (labels as Record<string, Record<string, string>>) ?? undefined,
  });
}

// ─── CSS var derivation ─────────────────────────────────────────────────

/** Resolve the accent value (preset or hex) to an HSL triplet `"h s% l%"`. */
export function accentToHsl(accent: string, isDark: boolean): string {
  if (isValidHex(accent)) {
    const hsl = hexToHsl(accent);
    if (hsl) return `${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}%`;
  }
  const theme = ACCENT_COLOR_THEMES[accent] ?? ACCENT_COLOR_THEMES.emerald;
  return isDark ? theme.dark : theme.light;
}

/** Resolve a `#RRGGBB` to an HSL triplet for CSS variable use. */
export function hexToHslVar(hex: string): string | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  return `${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}%`;
}

/**
 * Resolve a brand's effective theme to a concrete `"light" | "dark"`.
 * `"auto"` consults `prefers-color-scheme` when available, defaulting to
 * light during SSR.
 */
export function effectiveTheme(theme: ThemeMode): "light" | "dark" {
  if (theme === "light" || theme === "dark") return theme;
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Build the CSS variable map applied to `document.documentElement`. */
export function toCssVars(brand: BrandConfig): Record<string, string> {
  const isDark = effectiveTheme(brand.theme) === "dark";
  const primary = accentToHsl(brand.accent, isDark);
  const font = FONT_PRESETS[brand.font] ?? FONT_PRESETS["space-grotesk"];
  const density = DENSITY_PRESETS[brand.density];
  const radius = RADIUS_PRESETS[brand.radius];

  const vars: Record<string, string> = {
    "--primary": primary,
    "--ring": primary,
    "--accent": primary,
    "--sidebar-primary": primary,
    "--sidebar-ring": primary,
    "--sidebar-accent": primary,
    "--radius": radius,
    "--font-display": font.display,
    "--font-sans": font.body,
    "--density-input-h": density.inputH,
    "--density-gap": density.gap,
  };

  if (brand.surface) {
    const surfaceHsl = hexToHslVar(brand.surface);
    if (surfaceHsl) {
      vars["--card"] = surfaceHsl;
      vars["--popover"] = surfaceHsl;
    }
  }

  return vars;
}

/** Google Fonts CSS URL for the chosen preset, or null if no fetch needed. */
export function googleFontHref(font: FontPreset): string | null {
  const preset = FONT_PRESETS[font];
  if (!preset?.googleFamily) return null;
  return `https://fonts.googleapis.com/css2?family=${preset.googleFamily}&display=swap`;
}

// ─── Snippet helper ─────────────────────────────────────────────────────

/**
 * Build the query-string suffix for a brand. Omits fields that match the
 * default so existing simple embeds keep their short, readable URLs.
 */
export function buildBrandQuery(brand: PartialBrand): string {
  const r = resolveBrand(brand);
  const parts: string[] = [];
  const push = (k: string, v: string | undefined) => {
    if (v !== undefined && v !== "") parts.push(`${k}=${encodeURIComponent(v)}`);
  };
  // Always emit theme + accent for backwards compatibility with existing snippets.
  push("theme", r.theme);
  push("accent", r.accent);
  if (r.surface) push("surface", r.surface);
  if (r.font !== DEFAULT_BRAND.font) push("font", r.font);
  if (r.radius !== DEFAULT_BRAND.radius) push("radius", r.radius);
  if (r.density !== DEFAULT_BRAND.density) push("density", r.density);
  if (r.locale !== DEFAULT_BRAND.locale) push("locale", r.locale);
  if (r.units !== DEFAULT_BRAND.units) push("units", r.units);
  if (r.currency) push("currency", r.currency);
  return parts.join("&");
}
